import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import { toast } from 'sonner';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { MapPin, Loader2, Hash, X, CheckCircle2, Search, RotateCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_RADIUS = '0.1';
const REQUEST_TIMEOUT = 15000; // ms — so a slow/hung Zoho or Barbour call never spins forever

/**
 * "Add Barbour ID" — shown next to a project search result that's missing a
 * Barbour ID. Auto-looks up the project's own postcode against Barbour (0.1
 * mile radius) if one is on file; if not (or the user wants to adjust it),
 * the postcode/radius fields are always editable so they can search
 * manually. Clicking a match writes it back to the Zoho Deal (`PATCH
 * /zoho/projects/{id}/barbour-id`).
 */
export const AddBarbourIdModal = ({ isOpen, onClose, projectId, projectName, onAdded }) => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [alreadyHasId, setAlreadyHasId] = useState('');
  const [hadInitialPostcode, setHadInitialPostcode] = useState(false);
  const [postcodeInput, setPostcodeInput] = useState('');
  const [radiusInput, setRadiusInput] = useState(DEFAULT_RADIUS);
  const [maxRadius, setMaxRadius] = useState(5);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [searchError, setSearchError] = useState('');
  const [applyingId, setApplyingId] = useState(null);

  const runSearch = useCallback(async (pc, radius) => {
    const trimmed = (pc || '').trim();
    if (!trimmed) return;
    setSearching(true);
    setSearchError('');
    setResults(null);
    try {
      const useDirectRes = await api.get(`${API}/barbour/use-direct-api`, { timeout: REQUEST_TIMEOUT }).catch(() => ({ data: { use_direct_api: true } }));
      const apiPath = useDirectRes.data?.use_direct_api ? '/barbour/search' : '/barbour/search-v2';
      const res = await api.post(`${API}${apiPath}`, { postcode: trimmed, radius_miles: parseFloat(radius) || 0.1 }, { timeout: REQUEST_TIMEOUT });
      setResults(res.data);
    } catch (e) {
      setSearchError(e?.code === 'ECONNABORTED' ? 'Barbour search timed out — try again.' : (e?.response?.data?.detail || 'Barbour search failed'));
    } finally {
      setSearching(false);
    }
  }, []);

  const loadProject = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setLoadError('');
    setAlreadyHasId('');
    setResults(null);
    setSearchError('');
    setPostcodeInput('');
    setRadiusInput(DEFAULT_RADIUS);
    setHadInitialPostcode(false);
    try {
      const [projRes, maxRadiusRes] = await Promise.all([
        api.get(`${API}/zoho/project/${projectId}`, { timeout: REQUEST_TIMEOUT }),
        api.get(`${API}/barbour/max-radius`, { timeout: REQUEST_TIMEOUT }).catch(() => ({ data: { max_radius_miles: 5 } })),
      ]);
      setMaxRadius(maxRadiusRes.data?.max_radius_miles ?? 5);
      const project = projRes.data?.project;
      if (project?.barbour_project_id) {
        setAlreadyHasId(project.barbour_project_id);
        return;
      }
      const pc = (project?.postcode || '').trim();
      setPostcodeInput(pc);
      if (pc) {
        setHadInitialPostcode(true);
        await runSearch(pc, DEFAULT_RADIUS);
      }
    } catch (e) {
      setLoadError(e?.code === 'ECONNABORTED' ? 'Loading this project timed out — try again.' : (e?.response?.data?.detail || 'Failed to load project'));
    } finally {
      setLoading(false);
    }
  }, [projectId, runSearch]);

  useEffect(() => {
    if (!isOpen || !projectId) return;
    loadProject();
  }, [isOpen, projectId, loadProject]);

  if (!isOpen) return null;

  const applyResult = async (barbourId) => {
    setApplyingId(barbourId);
    try {
      await api.patch(`${API}/zoho/projects/${projectId}/barbour-id`, { barbour_id: barbourId }, { timeout: REQUEST_TIMEOUT });
      toast.success(`Barbour ID ${barbourId} added`);
      onAdded?.(barbourId);
      onClose?.();
    } catch (e) {
      toast.error(e?.code === 'ECONNABORTED' ? 'Timed out — try again.' : (e?.response?.data?.detail || 'Failed to update Barbour ID'));
    } finally {
      setApplyingId(null);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[85] flex items-center justify-center bg-black/70 backdrop-blur-sm p-3" data-testid="add-barbour-id-modal">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-xl max-h-[85vh] overflow-y-auto flex flex-col">
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="min-w-0">
            <h2 className="text-base font-extrabold text-gray-900 truncate">Add Barbour ID</h2>
            <p className="text-[11px] text-gray-500 truncate">{projectName}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100 flex-shrink-0" data-testid="add-barbour-id-modal-close">
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          {loading && (
            <div className="text-center text-gray-400 py-8">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              Loading project…
            </div>
          )}

          {!loading && loadError && (
            <div className="text-center py-6" data-testid="add-barbour-id-load-error">
              <p className="text-sm text-red-600 mb-3">{loadError}</p>
              <Button variant="outline" onClick={loadProject} data-testid="add-barbour-id-retry-btn">
                <RotateCw className="w-3.5 h-3.5 mr-1.5" /> Retry
              </Button>
            </div>
          )}

          {!loading && !loadError && alreadyHasId && (
            <p className="text-sm text-gray-600 text-center py-4" data-testid="add-barbour-id-already-set">
              This project already has Barbour ID <strong>{alreadyHasId}</strong>.
            </p>
          )}

          {!loading && !loadError && !alreadyHasId && (
            <>
              {!hadInitialPostcode && (
                <p className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="add-barbour-id-no-postcode-notice">
                  This project has no postcode on file — enter one below to search Barbour manually.
                </p>
              )}

              <div className="flex flex-wrap gap-2 items-end">
                <div className="flex-1 min-w-[160px]">
                  <Label className="text-xs text-gray-500 mb-1 block">Postcode</Label>
                  <Input
                    placeholder="e.g. B98 8DD"
                    value={postcodeInput}
                    onChange={(e) => setPostcodeInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') runSearch(postcodeInput, radiusInput); }}
                    autoFocus={!hadInitialPostcode}
                    data-testid="add-barbour-id-postcode-input"
                  />
                </div>
                <div className="w-24">
                  <Label className="text-xs text-gray-500 mb-1 block">Radius (mi)</Label>
                  <Input
                    type="number"
                    min="0.05"
                    max={maxRadius}
                    step="0.05"
                    value={radiusInput}
                    onChange={(e) => setRadiusInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') runSearch(postcodeInput, radiusInput); }}
                    data-testid="add-barbour-id-radius-input"
                  />
                </div>
                <Button
                  onClick={() => runSearch(postcodeInput, radiusInput)}
                  disabled={searching || !postcodeInput.trim()}
                  data-testid="add-barbour-id-search-btn"
                >
                  {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-3.5 h-3.5 mr-1" /> Search</>}
                </Button>
              </div>

              {searchError && (
                <p className="text-sm text-red-600" data-testid="add-barbour-id-error">{searchError}</p>
              )}

              {results && !searchError && (
                <>
                  <p className="text-xs text-gray-500">
                    Found {results.count} of {results.total_matching} project{results.total_matching === 1 ? '' : 's'} within {results.radius_miles} mile{results.radius_miles === 1 ? '' : 's'} of {results.postcode}. Click the correct match to add it.
                  </p>
                  {results.projects.length === 0 ? (
                    <p className="text-sm text-gray-400 py-6 text-center">No Barbour projects found in that radius.</p>
                  ) : (
                    <div className="divide-y divide-gray-100 border border-gray-200 rounded-xl">
                      {results.projects.map((p) => (
                        <button
                          key={p.barbour_id}
                          onClick={() => applyResult(p.barbour_id)}
                          disabled={applyingId !== null}
                          className="w-full text-left px-4 py-3 hover:bg-teal-50 transition-colors flex items-center justify-between gap-3 group"
                          data-testid={`add-barbour-id-result-${p.barbour_id}`}
                        >
                          <div className="min-w-0">
                            <div className="font-semibold text-sm text-gray-900 truncate">{p.project_name}</div>
                            <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5 flex-wrap">
                              <Hash className="w-3 h-3" />{p.barbour_id}
                              <MapPin className="w-3 h-3 ml-2" />{[p.address, p.postcode].filter(Boolean).join(', ')}
                            </div>
                          </div>
                          {applyingId === p.barbour_id
                            ? <Loader2 className="w-4 h-4 animate-spin text-teal-600 shrink-0" />
                            : <CheckCircle2 className="w-4 h-4 text-gray-300 group-hover:text-teal-600 shrink-0" />}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AddBarbourIdModal;
